/**
 * A utility class for rendering signature images.
 */
export default class SignatureImageRenderer
{
    constructor( /* Object */ props )
    {
        this._state = {
            /**
             * Fonts.
             *
             * @public Object
             */
            FONTS : {
                "name" : {
                    name : "LiberationSans",
                    size : 12,
                    color : "#333333"
                },
                "email" : {
                    name : "LiberationSans",
                    size : 10,
                    color : "#666666"
                },
                "timestamp" : {
                    name : "LiberationSans",
                    size : 12,
                    color : "#333333"
                }
            },
            /**
             * X-origin of the properties.
             *
             * @public int
             */
            x : 0,
            /**
             * Y-origin of the properties.
             *
             * @public int
             */
            y : 0,
            /**
             * Maximum width of the properties. When not set, defaults to canvas width.
             *
             * @public int
             */
            maxWidth : 0,
            /**
             * Font-size multiplier.
             *
             * @public int
             */
            scale : 1,
            /**
             * Value by which the image is scaled.
             *
             * @public number
             */
            zoom : 1,
            /**
             * Text align within canvas.
             *
             * @public string
             */
            textAlign : "center",
        }
        Object.assign( this._state, props );
    }

    /**
     * Render a signature image on given canvas with provided name and email. Returns the actual dimensions
     * of the properties.
     *
     * @param canvas {canvas}
     * @param propreties
     * @returns {Object} - {
     *     width: int - Actual width of the signature,
     *     height: int - Actual height of the signature
     * }
     */
    renderSignatureImage( canvas, properties )
    {
        this._context = canvas.getContext( "2d" );
        this._context.save();
        this._context.scale( this._state.zoom, this._state.zoom );
        this._context.textAlign = this._state.textAlign;
        if( this._state.maxWidth === 0 )
        {
            this._state.maxWidth = canvas.width;
        }
        let maxLineWidth = 0;
        //Fill background
        if( properties.shouldFillBackground )
        {
            this._context.fillStyle = "white";
            this._context.fillRect( 0, 0, canvas.width, canvas.height );
        }
        //Draw name
        properties.name = properties.name.trim();
        const nameFontSize = this._setFont( "name" );
        const nameTop = this._state.y + nameFontSize;
        let currentChar = 0;
        let nameLinesCount = 0;
        while( nameLinesCount < 3 && currentChar < properties.name.length )
        {
            let line = "";
            while( this._context.measureText( line + properties.name[ currentChar ] ).width
                   < this._state.maxWidth
                   && currentChar
                   < properties.name.length )
            {
                line += properties.name[ currentChar ];
                currentChar++;
            }
            let lineWidth = this._context.measureText( line ).width;
            maxLineWidth = Math.max( maxLineWidth, lineWidth );
            this._context.fillText( line, this._state.x, nameTop + nameLinesCount * nameFontSize );
            nameLinesCount++;
        }
        //Draw email
        properties.email = properties.email.trim();
        const emailParts = [];
        let part = "";
        for( var i = 0; i < properties.email.length; i++ )
        {
            if( ".-_@".indexOf( properties.email[ i ] ) !== -1 )
            {
                emailParts.push( part );
                emailParts.push( properties.email[ i ] );
                part = "";
            }
            else
            {
                part += properties.email[ i ];
            }
        }
        emailParts.push( part );
        const emailFontSize = this._setFont( "email" );
        const emailTop = nameTop + nameFontSize * nameLinesCount + emailFontSize / 3;
        let currentPart = 0;
        let emailLinesCount = 0;
        while( currentPart < emailParts.length && emailLinesCount < 3 )
        {
            let line = emailParts[ currentPart ];
            currentPart++;
            while( currentPart < emailParts.length &&
                   this._context.measureText( line + emailParts[ currentPart ] ).width < this._state.maxWidth )
            {
                line += emailParts[ currentPart ];
                currentPart++;
            }
            if( emailLinesCount === 2 && currentPart < emailParts.length - 1 )
            {
                line += emailParts[ currentPart ];
                line = this._truncateLine( line, "..." );
            }
            const lineWidth = this._context.measureText( line ).width;
            maxLineWidth = Math.max( maxLineWidth, lineWidth );
            this._context.fillText( line, this._state.x, emailTop + emailLinesCount * emailFontSize );
            emailLinesCount++;
        }
        //Draw timestamp
        const timestampFontSize = this._setFont( "timestamp" );
        const timestampTop = emailTop + ( emailLinesCount * emailFontSize ) + timestampFontSize;
        const timestampLineWidth = this._context.measureText( properties.timestamp ).width;
        maxLineWidth = Math.max( maxLineWidth, timestampLineWidth );
        this._context.fillText( properties.timestamp, this._state.x, timestampTop );
        this._context.restore();
        return {
            width : maxLineWidth * 1.15,
            height : ( timestampTop + timestampFontSize - this._state.y ) * 1.05
        };
    }

    /**
     * Sets a font specified by key. Returns font size.
     *
     * @param key {string}
     * @private
     * @returns {number}
     */
    _setFont( key )
    {
        const font = this._state.FONTS[ key ];
        this._context.fillStyle = font.color;
        const fontSize = font.size * this._state.scale;
        this._context.font = font.size * this._state.scale + "px " + font.name;
        return fontSize;
    }

    /**
     * Truncate line to fit into max width.
     *
     * @param line {string}
     * @param tail {string?}
     * @returns {string}
     * @private
     */
    _truncateLine( line, tail )
    {
        if( this._context.measureText( line ).width > this._state.maxWidth )
        {
            tail = tail || "";
            while( this._context.measureText( line + tail ).width > this._state.maxWidth )
            {
                line = line.slice( 0, -1 );
            }
            return line + tail;
        }
        return line;
    }
};